import styled from 'styled-components';
import { colors, spacing } from 'ui/theme';
import { Takeover as TakeoverUI } from 'ui/workflows/takeover';
import { pxToRem } from 'utils/convert';
import { TakeoverBody } from './TakeoverBody';
import { TakeoverFooter } from './TakeoverFooter';
import { useUploadAssetsContext } from '../../../contexts/UploadAssetsContext';
import { useTakeoverContext } from '../../../contexts/Takeover';
import { useTranslations } from '../../../hooks/useTranslations';
import { TakeoverIntent } from '../../../types';
import { hasNewUploads, hasEditChanges } from '../../../utils/images';
import { DeleteImageOverlay } from './DeleteImageOverlay';
import { DeleteImageProvider } from 'modules/creativeLibrary/contexts/DeleteImageOverlayContext';
import { Banner } from 'ui/banner';
import { FolderClosed } from 'ui/icons/action/FolderClosed';
import { BannerVariants } from 'ui/banner/types';
import { getEditFolderName, getUploadFolderName } from '../../../utils/folderHelpers';
import { useAssetsQueryContext } from 'modules/creativeLibrary/contexts/AssetsQueryContext';
import { ROOT_FOLDER_NAME } from 'modules/creativeLibrary/constants';
import { CloseTakeoverOverlay } from './CloseTakeoverOverlay';

export const BodyContainer = styled.div`
  color: ${({ theme }) => theme.colors.textColors.textPrimary}; /** legacy override */
  display: flex;
  flex: 1 1 auto;
  font-family: 'Sofia Pro', system-ui, -apple-system, BlinkMacSystemFont, 'Avenir Next', Avenir,
    'Segoe UI', 'Lucida Grande', 'Helvetica Neue', Helvetica, 'Fira Sans', Roboto, Noto,
    'Droid Sans', Cantarell, Oxygen, Ubuntu, 'Franklin Gothic Medium', 'Century Gothic',
    'Liberation Sans', sans-serif; /** legacy override */
  font-weight: 400; /** legacy override */
  position: relative;
  flex-direction: column;
  padding: ${spacing.plusHalf} ${spacing.base};
  gap: ${spacing.base};
`;

export const BodyContent = styled.div`
  bottom: 0;
  display: flex;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  
  /* Ensure child content respects the container width */
  & > * {
    max-width: 100%;
    min-width: 0;
    width: 100%;
  }
`;

const CenteredBannerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.quarter};
`;

const FolderName = styled.p`
  color: ${colors.textInfo};
  font-weight: 600;
`;

const FlexContainer = styled.div`
  position: relative;
  flex: 1 1 auto;
`;

const TakeoverContent = () => {
  const { t } = useTranslations();
  const { clearPendingUploads, pendingImages } = useUploadAssetsContext();
  const { currentIntent, isWarningOverlayOpen, onClose, onCloseWarningOverlay } =
    useTakeoverContext();
  const { currentFolder } = useAssetsQueryContext();
  const isEditMode = currentIntent === TakeoverIntent.EDIT;
  const hasContent = pendingImages.length || currentIntent === TakeoverIntent.EDIT;
  const warnBeforeClose = isEditMode ? hasEditChanges(pendingImages) : hasNewUploads(pendingImages);

  // If current folder is root, we don't want to show it in the banner
  const currentFolderName =
    currentFolder?.name === '__root__' ? undefined : currentFolder?.name || '';
  const uniquePendingImagesFolders = [
    ...new Set(pendingImages.map((pi) => pi.selected.destinationFolderName || currentFolderName)),
  ];

  const actualFolderName = isEditMode
    ? getEditFolderName(uniquePendingImagesFolders)
    : getUploadFolderName(uniquePendingImagesFolders, currentFolderName);

  return (
    <>
      <TakeoverUI
        Footer={hasContent ? <TakeoverFooter /> : null}
        isLoading={false}
        isMainApp={true}
        isOverlay={true}
        onClose={() => onClose(warnBeforeClose, clearPendingUploads)}
        size={`min(${pxToRem(976)}, 100%)`}
        title={
          currentIntent === TakeoverIntent.EDIT ? t('header.editImageDetails') : t('header.uploads')
        }
      >
        <BodyContainer>
          {actualFolderName && !isEditMode && (
            <Banner variant={BannerVariants.INFO}>
              <CenteredBannerContent>
                {t('banner.upload.title')}
                {actualFolderName !== ROOT_FOLDER_NAME && <FolderClosed />}
                <FolderName>{actualFolderName}</FolderName>
              </CenteredBannerContent>
            </Banner>
          )}
          <FlexContainer>
            <BodyContent>
              <TakeoverBody />
            </BodyContent>
          </FlexContainer>
        </BodyContainer>
        <DeleteImageOverlay />
        {isWarningOverlayOpen && (
          <CloseTakeoverOverlay
            isEditMode={isEditMode}
            onCancel={onCloseWarningOverlay}
            onConfirm={() =>
              onClose(false, () => {
                if (currentIntent === TakeoverIntent.UPLOAD) {
                  pendingImages.forEach((pi) => {
                    pi.abortUpload?.();
                  });
                }
                clearPendingUploads();
              })
            }
          />
        )}
      </TakeoverUI>
    </>
  );
};

export const Takeover = () => {
  return (
    <DeleteImageProvider>
      <TakeoverContent />
    </DeleteImageProvider>
  );
};
